import { segmentMst } from '@libs/domain';
import type { FieldErrorField } from '@ps-smart-cp/ppfw-client';
import { FieldSet, ItemArray, kebabCase, useItemName, useValidator } from '@ps-smart-cp/ppfw-client';
import type { Ref } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useMstSettings } from '../../../context';
import { segmentMstSettingFieldConfig } from '../item-config';
import type { SegmentMstSetting } from '../models';
import { registerSettings } from '../repositories';
import { SegmentSettingRow } from './SegmentSettingRow';

interface SegmentSettingFieldSetProps {
  errorFields?: string[];
}

interface SegmentSettingFieldSetRef {
  validate: () => FieldErrorField[];
  register: () => Promise<void>;
}

function InnerComponent(props: SegmentSettingFieldSetProps, ref: Ref<SegmentSettingFieldSetRef>) {
  const getName = useItemName();
  const { segmentMsts } = useMstSettings();
  const [settings, setSettings] = useState(segmentMsts);
  const validator = useValidator();
  const validate = () => {
    const usedIndex = settings.map((s, i) => (s.isUsed ? i : -1)).filter((i) => i !== -1);

    return validator
      .with({ settings }, { settings: ItemArray.of<SegmentMstSetting>(segmentMstSettingFieldConfig) })
      .execute()
      .filter((f) => usedIndex.some((i) => f.field.includes(i.toString())))
      .map((s) => ({ ...s, field: kebabCase(`segment-${s.field}`) }));
  };

  const register = async () =>
    registerSettings(settings, [
      getName(segmentMst.names.segment01),
      getName(segmentMst.names.segment02),
      getName(segmentMst.names.segment03),
      getName(segmentMst.names.segment04),
      getName(segmentMst.names.segment05),
    ]);

  useImperativeHandle(ref, () => ({ validate, register }), [settings]);

  return (
    <FieldSet legendProps={{ title: getName(segmentMst.names.segment), id: 'segment-field-set' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 100px repeat(2, 1fr) 2fr',
          gridTemplateRows: '26px repeat(5, 27px)',
        }}
      >
        <div style={{ display: 'contents' }}>
          <div style={{ gridArea: '1/2', textAlign: 'center' }}>
            {getName(segmentMstSettingFieldConfig.isUsed)}
          </div>
          <div style={{ gridArea: '1/3', textAlign: 'center' }}>
            {getName(segmentMstSettingFieldConfig.displayName)}
          </div>
          <div style={{ gridArea: '1/4', textAlign: 'center' }}>
            {getName(segmentMstSettingFieldConfig.codeDigits)}
          </div>
        </div>
        {settings.map((s) => {
          return (
            <SegmentSettingRow
              setting={s}
              setSetting={(c) =>
                setSettings(
                  settings.map((o) => {
                    if (o.segmentMstKey < s.segmentMstKey) {
                      return o;
                    } else if (o.segmentMstKey === s.segmentMstKey) {
                      return c;
                    } else if (c.isUsed) {
                      return o;
                    } else {
                      return { ...o, isUsed: false };
                    }
                  }),
                )
              }
              segmentMstKey={s.segmentMstKey}
              key={s.segmentMstKey}
              errorFields={props.errorFields}
              previousDisabled={
                s.segmentMstKey === 1
                  ? false
                  : !settings.find((p) => p.segmentMstKey === s.segmentMstKey - 1)?.isUsed
              }
            />
          );
        })}
        <div style={{ gridColumn: '5', gridRow: '1/6' }}></div>
      </div>
    </FieldSet>
  );
}

export const SegmentSettingFieldSet = forwardRef(InnerComponent);
export type { SegmentSettingFieldSetRef };
